import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Card,
  Filters,
  NotProvided,
  Page,
  PageHeader,
  Pagination,
  StatCard,
  StatCardList,
  Table,
  type Action,
  type FilterFieldSlot,
  type IconName,
} from '@evogard/ui';
import type { ClaimClosure } from '@evogard/contract';
import {
  CLAIM_CLOSURE_KINDS,
  CLAIM_CLOSURE_KIND_LABELS,
  CLAIM_CLOSURE_ORIGINS,
  claimClosureActionOf,
  claimClosurePhasesOf,
  count,
  formatDate,
  formatInstant,
  type ClaimClosureKind,
  type ClaimClosureOrigin,
} from '@evogard/domain';
import { useSessionContext } from '../../../lib/session.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { DataCell } from '../DataCell.js';
import {
  CLOSURE_KIND_TONE,
  CLOSURE_ORIGIN_LABEL,
  closurePhaseLabel,
} from './closureLabels.js';
import { ClaimDeskModal } from '../claim/ClaimDeskModal.js';
import { useClaim } from '../claim/useClaims.js';
import { PeripheralDeskModal } from '../peripherals/PeripheralDeskModal.js';
import { ReopenModal, closureNumberOf as numberOf } from './ReopenModal.js';
import {
  useClosureList,
  useClosurePermissions,
  type ClosureFilter,
} from './useClosures.js';

/** O ícone do contador de cada desfecho — cada conclusão com o seu (22/10/2026). */
// @valor — as chaves são os valores de `ClaimClosureKind`
const KIND_ICON: Readonly<Record<ClaimClosureKind, IconName>> = {
  concluido_reparo: 'veiculo',
  concluido_indenizacao: 'dinheiro',
  concluido_periferico: 'confirmado',
  concluido_somente_terceiro: 'terceiro',
  desistencia: 'sair',
  negado: 'bloqueado',
};

/**
 * Sinistros › Encerrados (13/10/2026) — os concluídos, as desistências e as
 * negativas da Análise, do Reparo, da Indenização e de Periféricos, numa lista
 * só, e o ato de reabrir.
 *
 * ## O que esta tela NÃO grava
 *
 * Encerrado não é tabela: é a fase de desfecho onde o sinistro (ou o
 * acionamento) está, lida pela regra de `@evogard/domain`
 * (`CLAIM_CLOSURE_PHASES`). O motivo e o parecer vêm do formulário da fase e do
 * pedido de desistência; o motivo de reabrir vira comentário do sinistro.
 *
 * ## O período vem da API
 *
 * Sem data escolhida, a lista é dos últimos 90 dias — e quem diz de quando é a
 * resposta (`since`), não um cálculo daqui: a tela mostra o recorte que o
 * servidor aplicou, e não o que ela acha que ele aplicou.
 */
export function ClosuresScreen() {
  const { data: context } = useSessionContext();
  const perms = useClosurePermissions();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search).trim();
  const [filter, setFilter] = useState<Omit<ClosureFilter, 'search'>>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [reopening, setReopening] = useState<ClaimClosure | null>(null);
  const [peripheral, setPeripheral] = useState<number | null>(null);
  /** O sinistro aberto na mesa, só para consulta (22/10/2026). */
  const [claimId, setClaimId] = useState<number | null>(null);
  const opened = useClaim(claimId);

  const query = useClosureList({ ...filter, search: term || undefined }, page, perPage);
  const result = query.data;
  const hasFilter =
    term !== '' ||
    filter.kind !== undefined ||
    filter.origin !== undefined ||
    filter.since !== undefined ||
    filter.until !== undefined;

  const canOpen = (c: ClaimClosure) =>
    c.source === 'periferico' ? perms.canOpenPeripheral : perms.canOpenClaim;
  /* O histórico é a mesa do encerrado, só para consulta (22/10/2026): a
     trilha por onde ele passou, o formulário de cada fase, a Atividade, os
     anexos e as mensagens. Mudar alguma coisa pede reabrir. */
  const openHistory = (c: ClaimClosure) => {
    if (c.source === 'periferico') setPeripheral(c.id);
    else setClaimId(c.id);
  };

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'kind',
      label: 'Desfecho',
      placeholder: 'Todos',
      options: CLAIM_CLOSURE_KINDS.map((k) => ({
        value: k,
        label: CLAIM_CLOSURE_KIND_LABELS[k],
      })),
    },
    {
      type: 'selecao',
      name: 'origin',
      label: 'Origem',
      placeholder: 'Todas',
      options: CLAIM_CLOSURE_ORIGINS.map((o) => ({
        value: o,
        label: CLOSURE_ORIGIN_LABEL[o],
      })),
    },
    { type: 'data', name: 'since', label: 'Encerrados desde' },
    { type: 'data', name: 'until', label: 'Até' },
  ];

  const period = result
    ? result.until
      ? `de ${formatDate(result.since)} a ${formatDate(result.until)}`
      : filter.since
        ? `desde ${formatDate(result.since)}`
        : `nos últimos 90 dias, desde ${formatDate(result.since)}`
    : null;

  return (
    <Page measure="cheia">
      <PageHeader title="Encerrados">
        Os concluídos, as desistências e as negativas da Análise, do Reparo, da
        Indenização e de Periféricos de {context?.currentTenant.nome}. Daqui se vê de onde
        cada um saiu e por quê. A negativa e a desistência podem ser reabertas, e o
        concluído do Reparo pode acionar a garantia.
      </PageHeader>

      {/* Os contadores seguem o período e a origem, e não o filtro de desfecho —
          senão o desfecho escolhido zeraria os outros. Um por desfecho: cada
          conclusão é a sua (22/10/2026). */}
      <StatCardList>
        {CLAIM_CLOSURE_KINDS.map((k) => (
          <StatCard
            key={k}
            icon={KIND_ICON[k]}
            label={CLAIM_CLOSURE_KIND_LABELS[k]}
            value={result ? (result.counts[k] ?? 0) : undefined}
          />
        ))}
      </StatCardList>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={
            result === undefined
              ? 'Carregando…'
              : count(result.total, 'encerrado', 'encerrados')
          }
          descricao={period ? `Encerrados ${period}.` : undefined}
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar encerrados',
              placeholder: 'Número, nome ou placa',
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
            }}
            toolbar={
              <Filters
                values={{
                  kind: filter.kind ?? '',
                  origin: filter.origin ?? '',
                  since: filter.since ?? '',
                  until: filter.until ?? '',
                }}
                fields={fields}
                onApply={(values) => {
                  const text = (name: string) => String(values[name] ?? '') || undefined;
                  setFilter({
                    kind: text('kind') as ClaimClosureKind | undefined,
                    origin: text('origin') as ClaimClosureOrigin | undefined,
                    since: text('since'),
                    until: text('until'),
                  });
                  setPage(1);
                }}
              />
            }
            label="Sinistros e acionamentos encerrados"
            items={result?.items ?? []}
            loading={query.isLoading}
            skeletonRows={perPage}
            rowKey={(c) => `${c.source}-${c.id}`}
            rowTitle={(c) => `${numberOf(c)} · ${c.name ?? 'sem nome'}`}
            rowSubtitle={(c) =>
              `${CLAIM_CLOSURE_KIND_LABELS[c.kind]} em ${CLOSURE_ORIGIN_LABEL[c.origin]}`
            }
            rowIcon="historico"
            rowActions={(c): Action[] => [
              ...(canOpen(c)
                ? [
                    {
                      label: 'Ver histórico',
                      icon: 'historico' as const,
                      run: () => openHistory(c),
                    },
                  ]
                : []),
              ...(c.source === 'sinistro' && perms.canOpenClaim
                ? [
                    {
                      label: 'Ver aviso',
                      icon: 'documento' as const,
                      run: () =>
                        void navigate({
                          to: '/claims/$id',
                          params: { id: String(c.id) },
                        }),
                    },
                  ]
                : []),
              /* O ato que o desfecho aceita (22/10/2026): o concluído sem volta
                 não oferece nada. */
              ...(perms.canReopen && claimClosureActionOf(c.origin, c.phase)
                ? [
                    {
                      label:
                        claimClosureActionOf(c.origin, c.phase) === 'garantia'
                          ? 'Acionar garantia'
                          : 'Reabrir',
                      icon: 'atualizar' as const,
                      run: () => setReopening(c),
                    },
                  ]
                : []),
            ]}
            empty={
              hasFilter
                ? 'Nenhum encerrado com esses filtros. Isso é sobre o recorte, não sobre a base.'
                : 'Nenhum sinistro nem acionamento encerrado no período. Eles chegam aqui ' +
                  'quando vão para uma fase de desfecho: concluído, desistência ou negativa.'
            }
            columns={[
              {
                title: 'Número',
                alignment: 'inicio' as const,
                minWidth: '8rem',
                cell: (c) => (
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-semibold tabular-nums">{numberOf(c)}</span>
                    {c.source === 'periferico' ? (
                      <span className="text-xs text-texto-suave">Periférico</span>
                    ) : c.claimType === 'terceiro' ? (
                      <span className="text-xs text-texto-suave">
                        Terceiro{c.parentClaimId ? ` · do #${c.parentClaimId}` : ''}
                      </span>
                    ) : null}
                  </div>
                ),
              },
              {
                title: 'Associado ou terceiro',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (c) => (
                  <div className="flex min-w-0 flex-col">
                    {c.name ? (
                      <span className="truncate font-semibold">{c.name}</span>
                    ) : (
                      <NotProvided />
                    )}
                    {c.plate && (
                      <span className="font-mono text-xs tabular-nums text-texto-suave">
                        {c.plate}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                title: 'Desfecho',
                minWidth: '11rem',
                cell: (c) => (
                  <div className="flex min-w-0 flex-col items-start gap-1">
                    <Badge tone={CLOSURE_KIND_TONE[c.kind]}>
                      {CLAIM_CLOSURE_KIND_LABELS[c.kind]}
                    </Badge>
                    {/* A fase só quando o desfecho tem mais de uma — as negativas
                        da Análise. Nas outras ela repetiria o selo. */}
                    {claimClosurePhasesOf(c.origin, c.kind).length > 1 && (
                      <span className="truncate text-xs text-texto-suave">
                        {closurePhaseLabel(c.origin, c.phase)}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                title: 'Saiu de',
                minWidth: '12rem',
                cell: (c) => (
                  <div className="flex min-w-0 flex-col">
                    <span className="font-semibold">
                      {CLOSURE_ORIGIN_LABEL[c.origin]}
                    </span>
                    {c.fromPhase ? (
                      <span className="truncate text-xs text-texto-suave">
                        {closurePhaseLabel(c.origin, c.fromPhase)}
                      </span>
                    ) : null}
                  </div>
                ),
              },
              {
                title: 'Encerrado em',
                minWidth: '11rem',
                cell: (c) => (
                  <DataCell icon="calendario" hint={c.closedByName ?? undefined}>
                    <span className="flex min-w-0 flex-col">
                      <span className="tabular-nums">{formatInstant(c.closedAt)}</span>
                      {c.closedByName && (
                        <span className="truncate text-xs text-texto-suave">
                          {c.closedByName}
                        </span>
                      )}
                    </span>
                  </DataCell>
                ),
              },
              {
                title: 'Motivo',
                minWidth: '16rem',
                cell: (c) =>
                  c.reason || c.notes ? (
                    <span
                      className="line-clamp-2 text-sm"
                      title={[c.reason, c.notes].filter(Boolean).join('\n\n')}
                    >
                      {c.reason ?? c.notes}
                    </span>
                  ) : (
                    <NotProvided />
                  ),
              },
            ]}
          />
          {result && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={setPage}
              onPerPageChange={(n) => {
                setPerPage(n);
                setPage(1);
              }}
              label="encerrados"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}

      {reopening && (
        <ReopenModal closure={reopening} onClose={() => setReopening(null)} />
      )}
      <PeripheralDeskModal
        key={peripheral ?? 'fechado'}
        peripheralId={peripheral}
        onClose={() => setPeripheral(null)}
      />
      {/* A mesa abre quando a ficha chega: ela é a linha que a mesa pede. */}
      <ClaimDeskModal
        key={claimId ?? 'fechado'}
        claim={claimId !== null && opened.data ? opened.data : null}
        onClose={() => setClaimId(null)}
        onOpenClaim={(other) => setClaimId(other.id)}
        onMoved={() => setClaimId(null)}
        lastMove={null}
        onLastMoveDone={() => undefined}
      />
    </Page>
  );
}
